import React, { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useMediaQuery,
  useTheme,
  Collapse,
  Badge,
  Tooltip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard,
  Inventory,
  Category,
  WorkspacePremium,
  Business,
  ContactMail,
  Settings,
  ExitToApp,
  ChevronLeft,
  ExpandLess,
  ExpandMore,
  SwapHoriz,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";

const DRAWER_WIDTH = 280;
const DRAWER_WIDTH_COLLAPSED = 72;

const StyledDrawer = styled(Drawer)<{ $collapsed?: boolean }>`
  width: ${(props) =>
    props.$collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH}px;
  flex-shrink: 0;

  & .MuiDrawer-paper {
    width: ${(props) =>
      props.$collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH}px;
    background: linear-gradient(180deg, #1a1a1a 0%, #2d2d2d 100%);
    border-right: none;
    transition: width 0.3s ease;
    overflow-x: hidden;
    box-sizing: border-box;
  }
`;

const Logo = styled(Box)`
  padding: 20px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const NavSection = styled(Box)`
  padding: 8px 0;
`;

const NavSectionTitle = styled(Typography)`
  color: rgba(255, 255, 255, 0.4);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  padding: 16px 24px 8px;
`;

type NavItem = {
  id: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  children?: { id: string; label: string; path: string }[];
  badge?: number;
};

const navigationItems: NavItem[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: <Dashboard />,
    path: "/admin",
  },
  {
    id: "products",
    label: "Ürünler",
    icon: <Inventory />,
    path: "/admin/products",
  },
  {
    id: "catalog",
    label: "Katalog",
    icon: <Category />,
    children: [
      { id: "categories", label: "Kategoriler", path: "/admin/categories" },
      { id: "attributes", label: "Öznitelikler", path: "/admin/attributes" },
      { id: "migration", label: "Migrasyon", path: "/admin/migration" },
    ],
  },
  {
    id: "certificates",
    label: "Sertifikalar",
    icon: <WorkspacePremium />,
    path: "/admin/certificates",
  },
  {
    id: "company",
    label: "Firma",
    icon: <Business />,
    children: [
      { id: "company-info", label: "Firma Bilgileri", path: "/admin/company" },
      { id: "about", label: "Hakkımızda", path: "/admin/about" },
      { id: "references", label: "Referanslar", path: "/admin/references" },
    ],
  },
  {
    id: "contact",
    label: "İletişim",
    icon: <ContactMail />,
    path: "/admin/contact-page",
  },
];

const secondaryItems: NavItem[] = [
  {
    id: "content-editor",
    label: "İçerik Editörü",
    icon: <Settings />,
    path: "/admin/content",
  },
];

type Props = {
  children: React.ReactNode;
};

const AdminLayout: React.FC<Props> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>(["catalog", "company"]);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const userData = localStorage.getItem("user");
    const adminKey = localStorage.getItem("adminKey");

    if (!token || !userData || !adminKey) {
      navigate("/admin/login");
      return;
    }

    setUser(JSON.parse(userData));

    fetch("/api/content/ping", {
      headers: { "x-admin-key": adminKey },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error("unauthorized");
        }
      })
      .catch(() => {
        localStorage.removeItem("authToken");
        localStorage.removeItem("user");
        localStorage.removeItem("adminKey");
        localStorage.removeItem("rememberMe");
        navigate("/admin/login");
      });
  }, [navigate]);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setCollapsed(!collapsed);
    }
  };

  const handleMenuToggle = (id: string) => {
    setExpandedMenus((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]
    );
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    localStorage.removeItem("adminKey");
    localStorage.removeItem("rememberMe");
    navigate("/admin/login");
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    if (path === "/admin") {
      return location.pathname === "/admin";
    }
    return location.pathname.startsWith(path);
  };

  const renderNavItem = (item: NavItem) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus.includes(item.id);
    const active = isActive(item.path);

    return (
      <React.Fragment key={item.id}>
        <ListItem disablePadding sx={{ display: "block" }}>
          <ListItemButton
            onClick={() => {
              if (hasChildren) {
                handleMenuToggle(item.id);
              } else if (item.path) {
                handleNavigate(item.path);
              }
            }}
            sx={{
              minHeight: 48,
              px: collapsed && !isMobile ? 2.5 : 3,
              py: 1,
              mx: 1,
              borderRadius: 2,
              backgroundColor: active
                ? "rgba(211, 47, 47, 0.15)"
                : "transparent",
              "&:hover": {
                backgroundColor: active
                  ? "rgba(211, 47, 47, 0.2)"
                  : "rgba(255, 255, 255, 0.05)",
              },
            }}
          >
            <Tooltip title={collapsed && !isMobile ? item.label : ""} placement="right">
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: collapsed && !isMobile ? 0 : 2,
                  justifyContent: "center",
                  color: active ? "#d32f2f" : "rgba(255, 255, 255, 0.7)",
                }}
              >
                {item.badge ? (
                  <Badge badgeContent={item.badge} color="error">
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
            </Tooltip>
            {(!collapsed || isMobile) && (
              <>
                <ListItemText
                  primary={item.label}
                  sx={{
                    "& .MuiTypography-root": {
                      color: active ? "#fff" : "rgba(255, 255, 255, 0.85)",
                      fontWeight: active ? 600 : 400,
                      fontSize: "0.9rem",
                    },
                  }}
                />
                {hasChildren && (isExpanded ? <ExpandLess sx={{ color: "rgba(255,255,255,0.5)" }} /> : <ExpandMore sx={{ color: "rgba(255,255,255,0.5)" }} />)}
              </>
            )}
          </ListItemButton>
        </ListItem>

        {hasChildren && (!collapsed || isMobile) && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map((child) => {
                const childActive = isActive(child.path);
                return (
                  <ListItemButton
                    key={child.id}
                    onClick={() => handleNavigate(child.path)}
                    sx={{
                      pl: 6,
                      py: 0.75,
                      mx: 1,
                      borderRadius: 1.5,
                      backgroundColor: childActive
                        ? "rgba(211, 47, 47, 0.1)"
                        : "transparent",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                      },
                    }}
                  >
                    <ListItemText
                      primary={child.label}
                      sx={{
                        "& .MuiTypography-root": {
                          color: childActive
                            ? "#d32f2f"
                            : "rgba(255, 255, 255, 0.65)",
                          fontSize: "0.85rem",
                          fontWeight: childActive ? 500 : 400,
                        },
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawerContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Logo */}
      <Logo>
        <Box
          component="img"
          src="/brand/yanmazkapi-logo.png"
          alt="Yanmaz Kapı"
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            objectFit: "cover",
            objectPosition: "center 25%",
            display: "block",
          }}
        />
        {(!collapsed || isMobile) && (
          <Box>
            <Typography
              sx={{
                color: "white",
                fontWeight: 700,
                fontSize: "1rem",
                lineHeight: 1.2,
              }}
            >
              Yanmaz Kapı
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.5)",
                fontSize: "0.75rem",
              }}
            >
              Admin Panel
            </Typography>
          </Box>
        )}
      </Logo>

      {/* Main Navigation */}
      <NavSection sx={{ flex: 1, overflowY: "auto" }}>
        {(!collapsed || isMobile) && <NavSectionTitle>Ana Menü</NavSectionTitle>}
        <List>{navigationItems.map((item) => renderNavItem(item))}</List>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", my: 2 }} />

        {(!collapsed || isMobile) && <NavSectionTitle>Araçlar</NavSectionTitle>}
        <List>{secondaryItems.map((item) => renderNavItem(item))}</List>
      </NavSection>

      {/* Collapse Toggle (Desktop only) */}
      {!isMobile && (
        <Box sx={{ p: 2, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              color: "rgba(255,255,255,0.6)",
              width: "100%",
              borderRadius: 2,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.05)" },
            }}
          >
            <ChevronLeft
              sx={{
                transform: collapsed ? "rotate(180deg)" : "none",
                transition: "transform 0.3s",
              }}
            />
          </IconButton>
        </Box>
      )}
    </Box>
  );

  if (!user) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f5f5" }}>
      {/* Sidebar - Desktop */}
      {!isMobile && (
        <StyledDrawer variant="permanent" $collapsed={collapsed}>
          {drawerContent}
        </StyledDrawer>
      )}

      {/* Sidebar - Mobile */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              background: "linear-gradient(180deg, #1a1a1a 0%, #2d2d2d 100%)",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
        }}
      >
        {/* Top Bar */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            backgroundColor: "white",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Toolbar>
            {isMobile && (
              <IconButton
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2, color: "#333" }}
              >
                <MenuIcon />
              </IconButton>
            )}

            <Box sx={{ flexGrow: 1 }} />

            {/* User Menu */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                variant="body2"
                sx={{ color: "#666", display: { xs: "none", sm: "block" } }}
              >
                {user?.username || "Admin"}
              </Typography>
              <IconButton
                onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                sx={{ p: 0.5 }}
              >
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: "#d32f2f",
                    fontSize: "0.9rem",
                    fontWeight: 600,
                  }}
                >
                  {(user?.username || "A").charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
            </Box>

            <Menu
              anchorEl={userMenuAnchor}
              open={Boolean(userMenuAnchor)}
              onClose={() => setUserMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: { width: 200, mt: 1 },
              }}
            >
              <MenuItem onClick={() => navigate("/")}>
                <ListItemIcon>
                  <SwapHoriz fontSize="small" />
                </ListItemIcon>
                Siteye Git
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ color: "#d32f2f" }}>
                <ListItemIcon>
                  <ExitToApp fontSize="small" sx={{ color: "#d32f2f" }} />
                </ListItemIcon>
                Çıkış Yap
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Page Content */}
        <Box sx={{ p: { xs: 2, md: 3 } }}>{children}</Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
